import mux, { Options, PlayerId } from 'mux-embed';
import { Action, action, Thunk, thunk, ThunkOn, thunkOn, State } from 'easy-peasy';
import { PlayerStatus } from '../typings';
import { buildMuxMonitorData, createMuxErrorTranslator } from '../utils/mux-helpers';
import { StoreState } from './store';
import { ErrorCodes } from '../errors/ErrorCodes';

export interface MuxState {
  enabled: boolean;
  monitor: PlayerId | null;
  suspendedByChromecast: boolean;
  enable: Thunk<MuxState, void, any, StoreState>;
  initialize: Thunk<MuxState, void, any, StoreState>;
  createSession: Thunk<MuxState, void, any, StoreState>;
  destroySession: Thunk<MuxState, void, any, StoreState>;
  reset: Thunk<MuxState, void, any, StoreState>;
  setEnabled: Action<MuxState, boolean>;
  setMonitor: Action<MuxState, PlayerId | null>;
  setSuspendedByChromecast: Action<MuxState, boolean>;
  onConfigLoaded: ThunkOn<MuxState, void, StoreState>;
  onPlayerVideoChanged: ThunkOn<MuxState, void, StoreState>;
  onPlayerStatusChanged: ThunkOn<MuxState, PlayerStatus, StoreState>;
  onError: ThunkOn<MuxState, Error, StoreState>;
  onChromecastCasting: ThunkOn<MuxState, boolean, StoreState>;
  onPlayerDestroy: ThunkOn<MuxState, void, StoreState>;
}

const initialState: State<MuxState> = {
  enabled: false,
  monitor: null,
  suspendedByChromecast: false,
};

export const muxState: MuxState = {
  ...initialState,

  enable: thunk((actions) => {
    actions.setEnabled(true);
  }),

  initialize: thunk((actions, _, { getStoreState }) => {
    const storeState = getStoreState();

    if (!storeState.config.muxIsEnabled || !storeState.config.muxEnvKey) {
      return;
    }

    if (!storeState.mux.enabled) {
      actions.setEnabled(true);
    }

    if (storeState.player.videoItem) {
      actions.createSession();
    }
  }),

  createSession: thunk((actions, _, { getStoreState, getState }) => {
    const state = getState();
    const storeState = getStoreState();

    if (!state.enabled || !storeState.config.muxIsEnabled || !storeState.config.muxEnvKey) {
      return;
    }

    const videoElement =
      (storeState.player.videoEngine?.el().querySelector('video') as HTMLMediaElement | null) ||
      storeState.player.videoElement;

    if (!videoElement) {
      return;
    }

    const playerId: PlayerId = videoElement;

    if (state.monitor) {
      mux.destroyMonitor(state.monitor);
    }

    const options: Options = {
      debug: storeState.config.debug,
      disableCookies: storeState.config.muxDisableCookies,
      respectDoNotTrack: storeState.config.muxRespectDoNotTrack,
      automaticErrorTracking: true,
      errorTranslator: createMuxErrorTranslator(),
      data: buildMuxMonitorData(storeState),
    };

    mux.monitor(playerId, options);
    actions.setMonitor(playerId);
  }),

  destroySession: thunk((actions, _, { getState }) => {
    const state = getState();

    if (state.monitor) {
      mux.destroyMonitor(state.monitor);
    }

    actions.setMonitor(null);
  }),

  reset: thunk((actions, _, { getState }) => {
    const state = getState();
    actions.destroySession();
    Object.assign(state, { ...initialState });
  }),

  setEnabled: action((state, enabled) => {
    state.enabled = enabled;
  }),

  setMonitor: action((state, monitor) => {
    state.monitor = monitor;
  }),

  setSuspendedByChromecast: action((state, suspendedByChromecast) => {
    state.suspendedByChromecast = suspendedByChromecast;
  }),

  onConfigLoaded: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.config.load.successType];
    },
    function handler(actions, _, { getState, getStoreState }) {
      const state = getState();
      const storeState = getStoreState();

      if (!state.enabled) {
        return;
      }

      if (!storeState.config.muxIsEnabled || !storeState.config.muxEnvKey) {
        actions.setEnabled(false);
        return;
      }

      actions.initialize();
    }
  ),

  onPlayerVideoChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setVideoItem];
    },
    function handler(actions, _, { getState }) {
      const state = getState();

      if (state.enabled) {
        actions.createSession();
      }
    }
  ),

  onPlayerStatusChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setStatus];
    },
    function handler(actions, event, { getState }) {
      const state = getState();

      if (!state.enabled) {
        return;
      }

      switch (event.payload) {
        case PlayerStatus.Ended:
        case PlayerStatus.Stopped:
          actions.destroySession();
      }
    }
  ),

  onError: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.error.setError];
    },
    function handler(actions, event, { getStoreState, getState }) {
      const state = getState();
      const storeState = getStoreState();

      if (!state.enabled) return;
      if (!state.monitor) actions.createSession();

      mux.emit(storeState.player.videoElement ?? '#bod-video-player', 'error', {
        player_error_code: event.payload.code || ErrorCodes.DEFAULT,
        player_error_message: `[Web Video Player] ${event.payload.message}`,
        player_error_context: `[${storeState.config.videoGuid}] Error`,
        player_error_severity: 'fatal',
        player_error_business_exception: true,
      });
    }
  ),

  onChromecastCasting: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.chromecast.setCastSession, storeActions.chromecast.setCastState];
    },
    function handler(actions, _, { getStoreState, getState }) {
      const storeState = getStoreState();
      const state = getState();
      const { CONNECTED, CONNECTING } = cast.framework.CastState;
      const isCasting =
        !!storeState.chromecast.castSession ||
        storeState.chromecast.castState === CONNECTED ||
        storeState.chromecast.castState === CONNECTING;

      if (isCasting && state.enabled) {
        actions.destroySession();
        actions.setEnabled(false);
        actions.setSuspendedByChromecast(true);
        return;
      }

      if (!isCasting && state.suspendedByChromecast) {
        actions.setSuspendedByChromecast(false);
        actions.initialize();
      }
    }
  ),

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy.startType];
    },
    function handler(actions, _, { getState }) {
      const state = getState();

      if (state.enabled) {
        actions.reset();
      }
    }
  ),
};
